// src/brokerageadmin/QuoteyDashboard/InsurerDetailModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";

  import Button from "react-bootstrap/Button";

import { Bar, Line, Doughnut } from 'react-chartjs-2'; // Doughnut if you want to use it
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, ArcElement, Legend } from 'chart.js';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './QuoteyDashboard.css'; // Ensure CSS is linked
import GetAppIcon from "@material-ui/icons/GetApp"; // The download icon from Material-UI icons

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, ArcElement, Legend);

const InsurerDetailModal = ({ show, onClose, insurerName, data, isLoading, error, chartOptions,startDate,     
endDate,insurerCode        }) => {

    const [isDownloading, setIsDownloading] = useState(false);
    const [internalFilters, setInternalFilters] = useState({
        policyStartDate: null,
        policyEndDate: null,
        createdStartDate: null,
        createdEndDate: null,
        effectiveStartDate: null,
        effectiveEndDate: null
    });
    const [internalData, setInternalData] = useState(null);
    const [isLoadingInternal, setIsLoadingInternal] = useState(false);
    const [insurerStatsData, setInsurerStatsData] = useState(null);
    const [quotingActivityChart, setQuotingActivityChart] = useState(null);
    const [insurerBindsData, setInsurerBindsData] = useState(null);
    const [quotingStartDate, setQuotingStartDate] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date;
    });
    const [quotingEndDate, setQuotingEndDate] = useState(new Date());

    // Lock body scroll but allow modal content to scroll
    useEffect(() => {
        if (show) {
            // Prevent scrollbar on body
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            // Smart scroll prevention - allow scroll inside modal, prevent outside
            const preventScroll = (e) => {
                // Find the modal content element
                const modalContent = document.querySelector('.report-modal-content');

                // If scroll is happening inside the modal content, allow it
                if (modalContent && modalContent.contains(e.target)) {
                    return; // Allow scrolling inside modal
                }

                // Otherwise, prevent scrolling the background
                e.preventDefault();
            };

            // Add listeners to prevent background scroll
            window.addEventListener('wheel', preventScroll, { passive: false });
            window.addEventListener('touchmove', preventScroll, { passive: false });

            return () => {
                // Cleanup: restore scroll and remove listeners
                document.body.style.overflow = originalOverflow;
                window.removeEventListener('wheel', preventScroll);
                window.removeEventListener('touchmove', preventScroll);
            };
        }
    }, [show]);

    const handleDateFilterChange = (filterNameStart, filterNameEnd, dates) => {
        const [start, end] = dates || [null, null];
        setInternalFilters(prev => ({
            ...prev,
            [filterNameStart]: start,
            [filterNameEnd]: end
        }));
    };

    // Helper function to convert array of objects to CSV
    const convertToCSV = (objArray) => {
        if (!objArray || objArray.length === 0) return "";
        const array = typeof objArray !== "object" ? JSON.parse(objArray) : objArray;
        let str = "";
        const headers = Object.keys(array[0]);
        str += headers.join(",") + "\r\n";

        for (let i = 0; i < array.length; i++) {
            let line = "";
            for (let index in headers) {
                if (line !== "") line += ",";
                const value = array[i][headers[index]];
                line += value === undefined ? "" : `"${value}"`;
            }
            str += line + "\r\n";
        }
        return str;
    };

    // Helper function to filter data by date range
    const filterDataByDateRange = (data, startDate, endDate) => {
        if (!data || data.length === 0) return [];
        const start = moment(startDate);
        const end = moment(endDate);

        return data.filter((item) => {
            const policyBindDate = moment(item.policyBindDate, "YYYY-MM-DD");
            return policyBindDate.isBetween(start, end, "days", "[]");
        });
    };

    // CSV download function
    const downloadCSV = () => {
        // Check if we have quoting activity data
        if (!insurerStatsData || insurerStatsData.length === 0) {
            alert("No quoting activity data available to download.");
            return;
        }

        // Filter by date range
        const filteredData = insurerStatsData.filter(item => {
            const itemDate = new Date(item._id.date);
            return itemDate >= quotingStartDate && itemDate <= quotingEndDate;
        });

        if (filteredData.length === 0) {
            alert("No quoting activity data in selected date range. Please adjust the date filter.");
            return;
        }

        // Aggregate by date (same logic as chart formatting)
        const aggregatedData = filteredData.reduce((acc, item) => {
            if (!acc[item._id.date]) {
                acc[item._id.date] = { date: item._id.date, quote: 0, refer: 0, decline: 0 };
            }
            if (item._id.status === 'Quote') acc[item._id.date].quote += item.count;
            if (item._id.status === 'Refer') acc[item._id.date].refer += item.count;
            if (item._id.status === 'Decline') acc[item._id.date].decline += item.count;
            return acc;
        }, {});

        // Convert to array sorted by date
        const csvReadyData = Object.values(aggregatedData).sort((a, b) =>
            new Date(a.date) - new Date(b.date)
        );

        // Generate CSV
        const csvData = convertToCSV(csvReadyData);
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `insurerQuotingActivity_${insurerName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchFilteredData = useCallback(async () => {
        if (!insurerName) return;
        setIsLoadingInternal(true);
        try {
            let qp = [];
            // Date filters
            if (internalFilters.policyStartDate) qp.push(`policyStartDate=${internalFilters.policyStartDate.toISOString().split("T")[0]}`);
            if (internalFilters.policyEndDate) {
                const ied = new Date(internalFilters.policyEndDate);
                ied.setDate(ied.getDate() + 1);
                qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`);
            }
            if (internalFilters.createdStartDate) qp.push(`createdStartDate=${internalFilters.createdStartDate.toISOString().split("T")[0]}`);
            if (internalFilters.createdEndDate) {
                const ied = new Date(internalFilters.createdEndDate);
                ied.setDate(ied.getDate() + 1);
                qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`);
            }
            if (internalFilters.effectiveStartDate) qp.push(`effectiveStartDate=${internalFilters.effectiveStartDate.toISOString().split("T")[0]}`);
            if (internalFilters.effectiveEndDate) {
                const ied = new Date(internalFilters.effectiveEndDate);
                ied.setDate(ied.getDate() + 1);
                qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`);
            }

            const serviceUrl = process.env.REACT_APP_BASICSERVICE;
            const url = `${serviceUrl}/api/policies/getInsurerDetails/${encodeURIComponent(insurerName)}?${qp.join('&')}`;
            const token = localStorage.getItem("jwt");
            
            const response = await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json", "x-auth-token": token },
            });
            
            if (!response.ok) {
                throw new Error(`API Error (${response.status}): ${response.statusText}`);
            }
            
            const responseData = await response.json();
            setInternalData(responseData.stats);
        } catch (e) {
            console.error(`Error fetching filtered insurer details:`, e);
        } finally {
            setIsLoadingInternal(false);
        }
    }, [insurerName, internalFilters]);

    const applyInternalFilters = () => {
        fetchFilteredData();
    };

    const clearInternalFilters = () => {
        setInternalFilters({
            policyStartDate: null,
            policyEndDate: null,
            createdStartDate: null,
            createdEndDate: null,
            effectiveStartDate: null,
            effectiveEndDate: null
        });
        setInternalData(null);
    };

    const fetchInsurerQuotingActivity = useCallback(async () => {
        if (!insurerCode || !insurerName) return;
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASICSERVICE}/api/prospects/getInsurerStats`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': localStorage.getItem('jwt')
                    },
                    body: JSON.stringify({ insurercode: insurerCode, insurerName: insurerName })
                }
            );
            const result = await response.json();
            console.log('Insurer Stats:', result.stats?.length, 'records');
            console.log('Insurer Binder Breakdown:', result.insurerBindersBreakdown?.length, 'records');
            setInsurerStatsData(result.stats || []);
            setInsurerBindsData(result.insurerBindersBreakdown || []);
        } catch (error) {
            console.error('Error fetching insurer quoting activity:', error);
        }
    }, [insurerCode, insurerName]);

    const formatQuotingActivityChart = useMemo(() => {
        if (!insurerStatsData || insurerStatsData.length === 0) return null;

        // Filter by date range
        const filteredData = insurerStatsData.filter(item => {
            const itemDate = new Date(item._id.date);
            return itemDate >= quotingStartDate && itemDate <= quotingEndDate;
        });

        if (filteredData.length === 0) return null;

        const aggregatedData = filteredData.reduce((acc, item) => {
            if (!acc[item._id.date]) {
                acc[item._id.date] = { date: item._id.date, quote: 0, refer: 0, decline: 0 };
            }
            if (item._id.status === 'Quote') acc[item._id.date].quote += item.count;
            if (item._id.status === 'Refer') acc[item._id.date].refer += item.count;
            if (item._id.status === 'Decline') acc[item._id.date].decline += item.count;
            return acc;
        }, {});

        const tableEntries = Object.values(aggregatedData);
        const labels = tableEntries.map((item) => item.date);
        const quoteData = tableEntries.map((item) => item.quote);
        const referData = tableEntries.map((item) => item.refer);
        const declineData = tableEntries.map((item) => item.decline);

        return {
            labels,
            datasets: [
                {
                    label: 'Quote',
                    data: quoteData,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Refer',
                    data: referData,
                    backgroundColor: 'rgba(255, 206, 86, 0.6)',
                    borderColor: 'rgba(255, 206, 86, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Decline',
                    data: declineData,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1
                }
            ]
        };
    }, [insurerStatsData, quotingStartDate, quotingEndDate]);

    useEffect(() => {
        setQuotingActivityChart(formatQuotingActivityChart);
    }, [formatQuotingActivityChart]);

    useEffect(() => {
        console.log("InsurerDetailModal useEffect show:", show, "insurerCode:", insurerCode, "insurerName:", insurerName);
        if (show && insurerCode && insurerName) {
            console.log("wake up fetchInsurerQuotingActivity");
            fetchInsurerQuotingActivity();
        }
    }, [show, fetchInsurerQuotingActivity]);

    const hasInternalFilters = Object.values(internalFilters).some(value => value !== null);
    const displayData = internalData || data;
    const displayLoading = isLoadingInternal || isLoading;

    // Use a modified set of chart options for the modal,
    // especially for legend position, as space might be limited.
    const modalChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top', // Or 'right' if preferred for some charts
                labels: { boxWidth: 12, padding: 10 }
            },
            title: { display: false },
            tooltip: { // Re-add default tooltip behavior if needed
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y;
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: { beginAtZero: true },
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 45,
                    minRotation: 30,
                    font: { size: 10 }
                }
            }
        }
    };

    // Special chart options for the "Policies by Reason" chart to handle long labels
    const reasonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { boxWidth: 12, padding: 10 }
            },
            title: { display: false },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        // Show full reason text in tooltip title
                        return context[0].label;
                    },
                    label: function(context) {
                        return `Count: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            y: { beginAtZero: true },
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 0, // Keep labels horizontal
                    minRotation: 0,
                    font: { size: 9 },
                    callback: function(value, index, values) {
                        // Truncate long labels and add "..." 
                        const label = this.getLabelForValue(value);
                        if (label.length > 20) {
                            return label.substring(0, 20) + '...';
                        }
                        return label;
                    }
                }
            }
        }
    };

    // Prepare chart data from the 'data' prop
    const prepareChartData = (detailData) => {
        if (!detailData) return {};

        const policiesByVertical = {
            labels: detailData.byVertical?.map(item => item.name || 'Unknown') || [],
            datasets: [{
                label: 'Policies by Vertical',
                data: detailData.byVertical?.map(item => item.count || 0) || [],
                backgroundColor: '#FFCD56', // Distinct color
                borderColor: '#FFCD56',
                borderWidth: 1,
            }],
        };

        const policiesByMonthCreated = {
            labels: detailData.byMonthCreated?.map(item => item.month || 'Unknown') || [],
            datasets: [{
                label: 'New Policies (Monthly)',
                data: detailData.byMonthCreated?.map(item => item.count || 0) || [],
                borderColor: '#4BC0C0', // Distinct color
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 2,
                tension: 0.4,
                fill: true,
            }],
        };

        const policiesByBroker = {
            labels: detailData.byBroker?.map(item => item.name || 'Unknown') || [],
            datasets: [{
                label: 'Policies by Broker',
                data: detailData.byBroker?.map(item => item.count || 0) || [],
                backgroundColor: '#9966FF', // Distinct color
                borderColor: '#9966FF',
                borderWidth: 1,
            }],
        };

        // Policies by Payment Method for this insurer
        const policiesByPaymentMethod = {
            labels: detailData.policiesByPaymentMethod?.map(item => item.paymentMethod || 'Unknown') || [],
            datasets: [{
                label: 'Policies by Payment Method',
                data: detailData.policiesByPaymentMethod?.map(item => item.count || 0) || [],
                backgroundColor: ['#FF6384', '#4BC0C0', '#FFCD56', '#9966FF', '#FF9F40', '#36A2EB'], // Consistent colors
                borderColor: ['#FF6384', '#4BC0C0', '#FFCD56', '#9966FF', '#FF9F40', '#36A2EB'],
                borderWidth: 1,
            }],
        };

        // NEW: Policies by Reason for this insurer
        const policiesByReason = {
            labels: detailData.policiesByReason?.map(item => item.reason || 'No Reason') || [],
            datasets: [{
                label: 'Policies by Reason',
                data: detailData.policiesByReason?.map(item => item.count || 0) || [],
                backgroundColor: ['#A6C844', '#7B68EE', '#FFD700', '#20B2AA', '#B0C4DE', '#8A2BE2'], // Another set of distinct colors
                borderColor: ['#A6C844', '#7B68EE', '#FFD700', '#20B2AA', '#B0C4DE', '#8A2BE2'],
                borderWidth: 1,
            }],
        };


        return { policiesByVertical, policiesByMonthCreated, policiesByBroker, policiesByPaymentMethod, policiesByReason };
    };


    const { policiesByVertical, policiesByMonthCreated, policiesByBroker, policiesByPaymentMethod, policiesByReason } = prepareChartData(displayData);

    const handleDownloadCsv = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
    
        try {
          const queryParams = [`insurerName=${encodeURIComponent(insurerName)}`];
        //   if (filters.status && filters.status !== "all") {
        //     queryParams.push(`status=${filters.status}`);
        //   }
        //   if (filters.vertical) {
        //     queryParams.push(`vertical=${encodeURIComponent(filters.vertical)}`);
        //   }
          if (startDate) {
            queryParams.push(
              `startDate=${startDate.toISOString().slice(0, 10)}`
            );
          }
          if (endDate) {
            const inc = new Date(endDate);
            inc.setDate(inc.getDate() + 1);
            queryParams.push(`endDate=${inc.toISOString().slice(0, 10)}`);
          }
    
          const qs = queryParams.join("&");
          const serviceUrl = process.env.REACT_APP_BASICSERVICE;
          const url = `${serviceUrl}/api/prospects/download/insurer?${qs}`;
          const token = localStorage.getItem("jwt");
    
          const resp = await fetch(url, {
            method: "GET",
            headers: { "x-auth-token": token },
          });
          if (!resp.ok) {
            const txt = await resp.text();
            throw new Error(`Download failed: ${resp.status} ${txt}`);
          }
    
          const blob = await resp.blob();
          const dlUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = dlUrl;
    
          // parse filename from header
          const disp = resp.headers.get("content-disposition") || "";
          let fname = `prospects_${insurerName}.csv`;
          const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disp);
          if (m?.[1]) fname = m[1].replace(/['"]/g, "");
    
          a.download = fname;
          document.body.appendChild(a);
          a.click();
    
          window.URL.revokeObjectURL(dlUrl);
          a.remove();
        } catch (err) {
          console.error("Error downloading CSV:", err);
          alert("Failed to download report. See console for details.");
        } finally {
          setIsDownloading(false);
        }
      };

    // Early return after all hooks
    if (!show) return null;

    return (
        <div className="report-modal-overlay" onClick={onClose}>
            <div className="report-modal-content" onClick={e => e.stopPropagation()}>
                <button className="report-modal-close" onClick={onClose}>×</button>
                <h2>{insurerName} Details</h2>

                {/* Date Filter Section */}
                <div className="modal-filters">
                    <h4 className="modal-filters-title">Filter by Date Range</h4>
                    <div className="modal-filters-grid">
                        <div className="modal-filter-item">
                            <label className="modal-filter-label">Policy Date Range:</label>
                            <DatePicker
                                selected={internalFilters.policyStartDate}
                                onChange={(dates) => handleDateFilterChange('policyStartDate', 'policyEndDate', dates)}
                                startDate={internalFilters.policyStartDate}
                                endDate={internalFilters.policyEndDate}
                                selectsRange
                                placeholderText="Select policy date range"
                                className="form-control form-control-sm"
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                        <div className="modal-filter-item">
                            <label className="modal-filter-label">Created Date Range:</label>
                            <DatePicker
                                selected={internalFilters.createdStartDate}
                                onChange={(dates) => handleDateFilterChange('createdStartDate', 'createdEndDate', dates)}
                                startDate={internalFilters.createdStartDate}
                                endDate={internalFilters.createdEndDate}
                                selectsRange
                                placeholderText="Select created date range"
                                className="form-control form-control-sm"
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                        <div className="modal-filter-item">
                            <label className="modal-filter-label">Effective Date Range:</label>
                            <DatePicker
                                selected={internalFilters.effectiveStartDate}
                                onChange={(dates) => handleDateFilterChange('effectiveStartDate', 'effectiveEndDate', dates)}
                                startDate={internalFilters.effectiveStartDate}
                                endDate={internalFilters.effectiveEndDate}
                                selectsRange
                                placeholderText="Select effective date range"
                                className="form-control form-control-sm"
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                    </div>
                    <div className="modal-filters-buttons">
                        <Button 
                            variant="primary" 
                            size="sm" 
                            onClick={applyInternalFilters}
                            disabled={!hasInternalFilters || isLoadingInternal}
                        >
                            {isLoadingInternal ? 'Applying...' : 'Apply Filters'}
                        </Button>
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={clearInternalFilters}
                            disabled={!hasInternalFilters && !internalData}
                        >
                            Clear Filters
                        </Button>
                        {internalData && (
                            <span className="modal-filters-status">
                                ✓ Custom filters applied
                            </span>
                        )}
                    </div>
                </div>

                {displayLoading ? (
                    <div className="loading-cell"><div className="loading-spinner"></div>Loading data...</div>
                ) : error ? (
                    <div className="error-message">Error: {error}</div>
                ) : !displayData ? (
                    <p>No detailed data available for this insurer.</p>
                ) : (
                    <>
                        <div className="stats-overview">
                            <div className="stat-card"><h3>Total Policies</h3><p className="stat-value">{displayData.totalPolicies ?? 0}</p></div>
                            {/* <div className="stat-card"><h3>Bound Policies</h3><p className="stat-value">{displayData.totalBoundPolicies ?? 0}</p></div>
                            <div className="stat-card"><h3>Quoted Policies</h3><p className="stat-value">{displayData.totalQuotedPolicies ?? 0}</p></div> */}
                        </div>

                        <div className="charts-row">
                            <div className="chart-container">
                                <h3>Policies by Vertical</h3>
                                <div className="chart-wrapper">
                                    {policiesByVertical?.labels?.length > 0 ? (
                                        <Bar data={policiesByVertical} options={modalChartOptions} />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No vertical data available.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="chart-container">
                                <h3>New Policies (Monthly)</h3>
                                <div className="chart-wrapper">
                                    {policiesByMonthCreated?.labels?.length > 0 ? (
                                        <Line data={policiesByMonthCreated} options={modalChartOptions} />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No monthly data available.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="charts-row">
                            <div className="chart-container">
                                <h3>Top Brokers for {insurerName}</h3>
                                <div className="chart-wrapper">
                                    {policiesByBroker?.labels?.length > 0 ? (
                                        <Bar data={policiesByBroker} options={modalChartOptions} />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No broker data available.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* NEW CHART CONTAINER FOR POLICIES BY PAYMENT METHOD */}
                            <div className="chart-container">
                                <h3>Policies by Payment Method</h3>
                                <div className="chart-wrapper">
                                    {policiesByPaymentMethod?.labels?.length > 0 ? (
                                        <Bar data={policiesByPaymentMethod} options={modalChartOptions} />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No payment method data available.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* NEW ROW FOR POLICIES BY REASON CHART */}
                        <div className="charts-row">
                            <div className="chart-container full-width">
                                <h3>Policies by Reason</h3>
                                <div className="chart-wrapper" style={{ height: '400px' }}>
                                    {policiesByReason?.labels?.length > 0 ? (
                                        <Bar data={policiesByReason} options={reasonChartOptions} />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No policy reason data available.</p>
                                            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                                                This could mean no policies have reasons recorded, or no policies match the current filters.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* NEW ROW FOR INSURER QUOTING ACTIVITY CHART */}
                        <div className="charts-row">
                            <div className="chart-container full-width">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <h3>Insurer Quoting Activity (Quote/Refer/Decline)</h3>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <label style={{ fontSize: '14px', marginRight: '5px' }}>Date Range:</label>
                                        <DatePicker
                                            selectsRange
                                            startDate={quotingStartDate}
                                            endDate={quotingEndDate}
                                            onChange={(dates) => {
                                                const [start, end] = dates || [null, null];
                                                setQuotingStartDate(start);
                                                setQuotingEndDate(end);
                                            }}
                                            isClearable
                                            placeholderText="Select date range"
                                            dateFormat="yyyy-MM-dd"
                                            className="filter-select"
                                            style={{ fontSize: '12px' }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<GetAppIcon />}
                                            onClick={downloadCSV}
                                            disabled={!insurerStatsData || insurerStatsData.length === 0}
                                            style={{ fontSize: '12px', padding: '6px 12px' }}
                                        >
                                            Download CSV
                                        </Button>
                                    </div>
                                </div>
                                <div className="chart-wrapper" style={{ height: '400px' }}>
                                    {quotingActivityChart?.labels?.length > 0 ? (
                                        <Bar
                                            data={quotingActivityChart}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    x: {
                                                        beginAtZero: true,
                                                        stacked: true,
                                                        ticks: {
                                                            autoSkip: true,
                                                            maxRotation: 45,
                                                            minRotation: 30
                                                        }
                                                    },
                                                    y: {
                                                        beginAtZero: true,
                                                        stacked: true,
                                                        ticks: {
                                                            precision: 0
                                                        }
                                                    }
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'top',
                                                        labels: { boxWidth: 12, padding: 10 }
                                                    },
                                                    title: { display: false },
                                                    tooltip: {
                                                        mode: 'index',
                                                        intersect: false
                                                    }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div className="empty-data-cell">
                                            <p>No quoting activity data available.</p>
                                            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                                                This shows daily Quote/Refer/Decline responses from the insurer.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadCsv}
            disabled={isDownloading}
            size="small"
          >
            {isDownloading ? "Downloading…" : "Download CSV"}
          </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default InsurerDetailModal;